// src/components/ProdMgrs/components/ProdMgrsMonthlyCommissionsTable.jsx
// ============================================================================
// BLOCK PMGR.MC.0 — Monthly Commissions Table (Program Manager)
// ============================================================================

import React from 'react';
import PropTypes from 'prop-types';

import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import Typography from '@mui/material/Typography';

import axios from '../../../axiosClient.js';
// IMPORTANT:
// These components (based on your pasted originals) already render <TableRow>.
import MonthlyCommissionsHeader from '../../MonthlyCommissionsHeader.jsx';
import MonthlyCommissionRow from '../../MonthlyCommissionsRow.jsx';
import { buildPmgrConfigMap, computePmgrLineCalcs } from '../utils/pmgrCommissionMath.js';

// ============================================================================
// BLOCK PMGR.MC.1 — Month helpers (uppercase keys to match shared MONTHS)
// Notes:
//  - DO NOT use `new Date('YYYY-MM-DD')` here: it parses as UTC and shifts months
//    for America/Los_Angeles (and other timezones).
// ============================================================================

const MONTH_KEYS = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC',
];

function monthKeyFromDate(iso) {
  if (!iso || typeof iso !== 'string') {
    return null;
  }

  // Accept 'YYYY-MM-DD' or 'YYYY-MM-DDTHH:mm:ss...'
  const s = iso.trim();
  const ymd = s.slice(0, 10); // 'YYYY-MM-DD'
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    return null;
  }

  const mm = Number(ymd.slice(5, 7)); // 1..12
  if (!Number.isFinite(mm) || mm < 1 || mm > 12) {
    return null;
  }

  return MONTH_KEYS[mm - 1] || null;
}

// ============================================================================
// BLOCK PMGR.MC.2 — Payload normalizer (no nested ternaries)
// Purpose:
//  - Support multiple backend shapes while keeping ESLint happy.
// ============================================================================

function extractLineRows(linePayload) {
  if (!linePayload) {
    return [];
  }

  // Preferred: { programs: [], total: n }
  if (Array.isArray(linePayload.programs)) {
    return linePayload.programs;
  }

  // Back-compat: { commissions: [], total: n }
  if (Array.isArray(linePayload.commissions)) {
    return linePayload.commissions;
  }

  // Raw array
  if (Array.isArray(linePayload)) {
    return linePayload;
  }

  return [];
}

// ============================================================================
// BLOCK PMGR.MC.3 — Numeric coercion (prevents NaN)
// ============================================================================

function toNum(v) {
  if (v === null || v === undefined) {
    return 0;
  }
  if (typeof v === 'number') {
    return Number.isFinite(v) ? v : 0;
  }
  if (typeof v === 'string') {
    const cleaned = v.replace(/[$,]/g, '').trim();
    if (!cleaned) {
      return 0;
    }
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

// ============================================================================
// BLOCK PMGR.MC.4 — Aggregation helper
// Purpose:
//  - Convert computed line rows into the MonthlyCommissionRow-compatible shape
// IMPORTANT:
//  - cfgMap from buildPmgrConfigMap() is a PLAIN OBJECT, not a Map.
// ============================================================================

function aggregateMonthlyCommissions(computedLines, cfgMap) {
  const agg = new Map();
  const safeLines = Array.isArray(computedLines) ? computedLines : [];

  safeLines.forEach((r) => {
    const category = String(r?.ProductGroup ?? r?.Category ?? r?.CATEGORY ?? '').trim();
    if (!category) {
      return;
    }

    const mk = monthKeyFromDate(r?.InvoiceDate);
    if (!mk) {
      return;
    }

    if (!agg.has(category)) {
      const c = cfgMap ? cfgMap[category] : null;

      const base = toNum(c?.Base);
      const tPci = toNum(c?.T_PCI);
      const dPci = toNum(c?.D_PCI);
      const dSamp = toNum(c?.D_Samp);
      const repMult = toNum(c?.Rep_Mult);

      const computedMoBase = (base + repMult - dSamp * tPci) / 12;
      const computedMoGoal = (base + repMult + tPci * dPci - dSamp * tPci) / 12;

      const tier2Start =
        c?.moBase === null || c?.moBase === undefined ? computedMoBase : toNum(c.moBase);
      const tier3Start =
        c?.moGoal === null || c?.moGoal === undefined ? computedMoGoal : toNum(c.moGoal);

      agg.set(category, {
        category,

        // Rates shown in the row header columns
        tier1: toNum(c?.COMM1),
        tier2: toNum(c?.COMM2),
        tier3: toNum(c?.COMM3),

        // Thresholds used by TieredCell shading logic (if it expects them)
        tier2Start,
        tier3Start,

        // Month buckets (must match shared MONTHS keys)
        JAN: 0,
        FEB: 0,
        MAR: 0,
        APR: 0,
        MAY: 0,
        JUN: 0,
        JUL: 0,
        AUG: 0,
        SEP: 0,
        OCT: 0,
        NOV: 0,
        DEC: 0,

        total: 0,
      });
    }

    const o = agg.get(category);

    // computePmgrLineCalcs currently returns "lineCommission" (per your pmgrCommissionMath.js)
    // But we accept a few variants to be safe.
    const lc = toNum(
      r?.lineCommission ?? r?.LineCommissions ?? r?.LineCommission ?? r?.line_commission ?? 0
    );

    o[mk] = toNum(o[mk]) + lc;
  });

  // Compute totals for each row
  const out = Array.from(agg.values()).map((r) => {
    const total = MONTH_KEYS.reduce((sum, k) => sum + toNum(r[k]), 0);
    return { ...r, total };
  });

  // Add a "Total" summary row like original table
  if (out.length) {
    const totalsByMonth = {};
    MONTH_KEYS.forEach((k) => {
      totalsByMonth[k] = out.reduce((s, r) => s + toNum(r[k]), 0);
    });

    out.push({
      category: 'Total',
      tier1: 0,
      tier2: 0,
      tier3: 0,
      tier2Start: 0,
      tier3Start: 0,
      ...totalsByMonth,
      total: out.reduce((s, r) => s + toNum(r.total), 0),
    });
  }

  // Sort with Total at bottom
  return out.sort((a, b) => {
    if (a.category === 'Total') {
      return 1;
    }
    if (b.category === 'Total') {
      return -1;
    }
    return a.category.localeCompare(b.category);
  });
}

// ============================================================================
// BLOCK PMGR.MC.5 — Component
// ============================================================================

function ProdMgrsMonthlyCommissionsTable({ selectedRep, selectedYear }) {
  const [rows, setRows] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    // Reset on empty selection
    if (!selectedRep || !selectedYear) {
      setRows([]);
      setError(null);
      setIsLoading(false);
      return () => {};
    }

    let alive = true;

    async function load() {
      setIsLoading(true);
      setError(null);

      try {
        const [cfgRes, linesRes] = await Promise.all([
          axios.get('/api/prod-mgrs/quota-breakdown', {
            params: { rep: selectedRep, year: selectedYear },
          }),
          // ✅ use PMGR “programs” endpoint (line detail)
          axios.get('/api/prod-mgrs/programs', {
            params: {
              rep: selectedRep,
              year: selectedYear,
              fetchAll: 1,
              limit: 2147483647,
              offset: 0,
            },
          }),
        ]);

        const cfg = Array.isArray(cfgRes?.data) ? cfgRes.data : [];

        // buildPmgrConfigMap returns a plain object keyed by CATEGORY
        const cfgMap = buildPmgrConfigMap(cfg);

        const lineRows = extractLineRows(linesRes?.data);

        // computePmgrLineCalcs signature in your file: (rows, cfgMap)
        const computedLines = computePmgrLineCalcs(lineRows, cfgMap);
        const aggregated = aggregateMonthlyCommissions(computedLines, cfgMap);

        if (!alive) {
          return;
        }
        setRows(aggregated);
      } catch (e) {
        if (!alive) {
          return;
        }
        setError(e);
        setRows([]);
      } finally {
        if (alive) {
          setIsLoading(false);
        }
      }
    }

    load();

    return () => {
      alive = false;
    };
  }, [selectedRep, selectedYear]);

  const errorMsg =
    error?.response?.data?.error || error?.message || 'Failed to load monthly commissions.';

  return (
    <Paper elevation={3} sx={{ padding: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Monthly Commissions
      </Typography>

      {!selectedRep ? (
        <Alert severity="info">Select a rep to view monthly commissions.</Alert>
      ) : null}
      {error ? <Alert severity="error">{errorMsg}</Alert> : null}

      {isLoading ? (
        <CircularProgress size={28} />
      ) : (
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <MonthlyCommissionsHeader />
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <MonthlyCommissionRow key={r.category} monthCommission={r} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}

ProdMgrsMonthlyCommissionsTable.propTypes = {
  selectedRep: PropTypes.string.isRequired,
  selectedYear: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

export default ProdMgrsMonthlyCommissionsTable;
